import { useMemo } from 'react';
import type { HomepageConfig } from '@cng/shared';

/**
 * The announcement marquee. The original duplicates its item list once and
 * animates `translateX(0 → -50%)`, which makes the loop seamless; the same
 * trick is reproduced here so the motion matches.
 */
export function Ticker({ config }: { config: HomepageConfig['ticker'] }) {
  const now = Date.now();

  const items = useMemo(
    () => (config?.items ?? [])
      .filter((i) => i.enabled)
      .filter((i) => !i.startsAt || new Date(i.startsAt).getTime() <= now)
      .filter((i) => !i.endsAt || new Date(i.endsAt).getTime() >= now)
      .sort((a, b) => a.order - b.order),
    [config?.items, now],
  );

  if (!config?.enabled || items.length === 0) return null;

  const rendered = [...items, ...items];

  return (
    <div className="cg9-strip" role="region" aria-label="Announcements">
      <div
        className="cg9-track"
        style={{ animationDuration: `${config.speedSeconds ?? 55}s` }}
      >
        {rendered.map((item, index) => {
          const key = `${item.text}-${index}`;
          const body = (
            <>
              {item.icon ? <span className="cg9-star" aria-hidden="true">{item.icon}</span> : null}
              {item.live ? (
                <span className="cg9-live">
                  <span className="ld" aria-hidden="true" />
                  {item.liveLabel || 'Now Open'}
                </span>
              ) : null}
              {/* Stored copy carries <b> emphasis exactly as the original marquee did. */}
              <span dangerouslySetInnerHTML={{ __html: item.html || item.text }} />
              <span className="cg9-sep" aria-hidden="true" />
            </>
          );

          return item.link ? (
            <a
              className="cg9-mi"
              key={key}
              href={item.link}
              aria-hidden={index >= items.length ? true : undefined}
              tabIndex={index >= items.length ? -1 : undefined}
            >
              {body}
            </a>
          ) : (
            <span
              className="cg9-mi"
              key={key}
              aria-hidden={index >= items.length ? true : undefined}
            >
              {body}
            </span>
          );
        })}
      </div>
    </div>
  );
}
